import { useTheme } from "next-themes";
import { useEffect, useRef } from "react";

const MEDIA = "(prefers-color-scheme: dark)";

/**
 * next-themes applies the `light` / `dark` class from `prefers-color-scheme` when
 * the stored theme is `system`, but that can get out of sync (effect ordering,
 * WebView quirk, or resuming the app after the OS theme changed).
 * Re-sync the document to match `matchMedia` while `theme === "system"`.
 */
function reconcileSystemToMedia(themeIsSystem: boolean) {
  if (!themeIsSystem) {
    return;
  }
  const prefersDark = window.matchMedia(MEDIA).matches;
  const root = document.documentElement;
  const isDark = root.classList.contains("dark");
  if (prefersDark === isDark) {
    return;
  }
  root.classList.remove("light", "dark");
  root.classList.add(prefersDark ? "dark" : "light");
  root.style.colorScheme = prefersDark ? "dark" : "light";
}

/**
 * `theme-color` should follow the actual OS / browser appearance when the user
 * picked System; `resolvedTheme` from context can lag or disagree with
 * `prefers-color-scheme` in that mode.
 */
function useThemeColorMeta() {
  const { theme, resolvedTheme } = useTheme();

  useEffect(() => {
    const setMeta = (isDark: boolean) => {
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) {
        meta.setAttribute("content", isDark ? "#0a0a0a" : "#ffffff");
      }
    };

    if (theme !== "system") {
      setMeta(resolvedTheme === "dark");
      return;
    }

    const mql = window.matchMedia(MEDIA);
    const update = () => setMeta(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, [theme, resolvedTheme]);
}

/**
 * After next-themes runs, ensure `html.light` / `html.dark` matches
 * `prefers-color-scheme` whenever the app theme is "system".
 */
function useReconcileSystemClass() {
  const { theme } = useTheme();
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    if (theme !== "system") {
      return;
    }

    const run = () => reconcileSystemToMedia(themeRef.current === "system");

    run();
    const t = window.setTimeout(run, 0);

    const mql = window.matchMedia(MEDIA);
    mql.addEventListener("change", run);
    const onVis = () => {
      if (document.visibilityState === "visible") {
        run();
      }
    };
    const onFocus = () => run();

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);

    return () => {
      clearTimeout(t);
      mql.removeEventListener("change", run);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, [theme]);
}

/**
 * Subscribes in-app theme + OS appearance for correct colors and PWA bar tint.
 * Must render as a child of `ThemeProvider` (e.g. in `main.tsx`).
 */
export function SystemThemeSync() {
  useReconcileSystemClass();
  useThemeColorMeta();
  return null;
}
